import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../../../config";
import {
  getPeriodRanges,
  summarizeNewCustomers,
  pageCountForTotal,
  buildCacheKey,
} from "./newCustomersSecuredHelpers";
import "./NewCustomersSecured.css";

// "New Customers Secured" widget — counts of newly secured customers (distinct
// newly-appearing Costar Property Ids) for Week / Month-To-Date / Year-To-Date.
//
// Role behaviour is driven by the same dataScope contract every dashboard widget
// uses (no role names hardcoded):
//   dataScope === "self" (Sales): only the logged-in user's data — Week / MTD /
//     YTD shown as KPI cards.
//   dataScope === "all" (Sales Director / President): overall MTD + YTD totals,
//     then a per-sales-representative breakdown (Rep | MTD | YTD).
//
// The newly-secured determination is computed client-side from GetCustomerInfo
// (`costarId` + `createdOn`); see newCustomersSecuredHelpers. A single
// year-to-date fetch feeds all three windows.

// Fetch every GetCustomerInfo record in the year-to-date window. Capped so a
// bad/huge response can never fan out unbounded.
const PAGE_SIZE = 200;
const MAX_PAGES = 50;
// const REQUEST_TIMEOUT_MS = 60000;

// Module-level cache of computed summaries, keyed by user + scope + YTD window
// (buildCacheKey). A revisit to the dashboard renders instantly instead of
// re-running the multi-page fetch; the key is day-scoped so it self-invalidates.
const summaryCache = new Map();

const rowsOf = (res) => {
  const data = res && res.data ? res.data : {};
  return Array.isArray(data.result) ? data.result : [];
};

// Page 1 is fetched first to learn TotalCount, then the remaining pages are
// requested in parallel (instead of one-at-a-time) to cut load time. `signal`
// aborts the in-flight requests on unmount, and each request has a timeout so a
// hung page can't leave the widget stuck on "Loading…".
const fetchAllNewCustomerRecords = async ({ token, userId, salesPersonId, signal }) => {
  const { year } = getPeriodRanges();
  const getPage = (pageIndex) =>
    axios.get(`${API_URL}/User/api/CustomerInfo/GetCustomerInfo`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        PageIndex: pageIndex,
        PageSize: PAGE_SIZE,
        userId,
        sort: "asc",
        FromDate: year.fromDate,
        ToDate: year.toDate,
        ...(salesPersonId ? { SalesPersonId: salesPersonId } : {}),
      },
      signal,
      // timeout: REQUEST_TIMEOUT_MS,
    });

  const first = await getPage(1);
  const firstRows = rowsOf(first);
  const total = first && first.data ? first.data.TotalCount : undefined;
  const pages = pageCountForTotal(total, PAGE_SIZE, MAX_PAGES);
  if (pages <= 1 || firstRows.length === 0) return firstRows;

  // Pages 2..N concurrently (the browser caps real per-host concurrency).
  const rest = await Promise.all(
    Array.from({ length: pages - 1 }, (_, i) => getPage(i + 2))
  );
  return rest.reduce((acc, res) => acc.concat(rowsOf(res)), firstRows.slice());
};

const NewCustomersSecured = ({ auth: { auth }, dataScope = "self", userId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
   const isSelf = dataScope === "self";
  const token = auth && auth[0] ? auth[0].token : null;
  let tmpUserId = userId;
  if(auth && (auth[0]?.roleName ==='Sales' && auth[0]?.salesPersonId !=='')){
    tmpUserId = auth[0]?.salesPersonId
  }
  const salesPersonId = isSelf ? tmpUserId : null;
  useEffect(() => {
    if (!token) return undefined;
    const { year } = getPeriodRanges();
    const cacheKey = buildCacheKey({ userId, salesPersonId, range: year });

    // Memoized: a summary already computed for this user/scope/day renders
    // immediately, with no refetch.
    const cached = summaryCache.get(cacheKey);
    if (cached) {
      setSummary(cached);
      setError(false);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchAllNewCustomerRecords({ token, userId, salesPersonId, signal: controller.signal })
      .then((records) => {
        if (cancelled) return;
        const result = summarizeNewCustomers(records);
        summaryCache.set(cacheKey, result);
        setSummary(result);
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        console.warn("Could not load New Customers Secured:", err.message);
        setError(true);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, userId, salesPersonId]);

  const totals = summary ? summary.totals : { week: 0, mtd: 0, ytd: 0 };
  const reps = summary ? summary.reps : [];

  // Sales (self) scopes the listing to the logged-in user; Director/President
  // ("all") drill into every rep's customers for the window.
  const scopeExtra = isSelf ? { salesPersonId: userId } : {};

  const ranges = getPeriodRanges();

  const openListing = (range, extra, itemSalesPersonId = {}) => {
    const params = new URLSearchParams({
      fromDate: range.fromDate,
      toDate: range.toDate
    });
    if(itemSalesPersonId){
      params.set('salesPersonId', itemSalesPersonId);
    }
    if(salesPersonId){
      params.set('salesPersonId', salesPersonId);
    }
    // Object.entries(extra).forEach(([k, v]) => {
    //   if (v) params.set(k, v);
    // });
    navigate(`/new-customers-secured-info?${params.toString()}`);
    // const url = `/new-customers-secured-info?${params.toString()}`;
    // window.open(url, "_blank");
    // window.open(
    //   `${window.location.origin}/new-customers-secured-info?${params.toString()}`,
    //   "_blank",
    //   "noopener,noreferrer"
    // );

  };

  // A KPI card; only clickable (drills into the listing) when its count > 0.
  const renderCard = (label, count, range, extra) => {
    const clickable = count > 0;
    return (
      <div
        className={`ncs-card${clickable ? " ncs-clickable" : ""}`}
        onClick={clickable ? () => openListing(range, extra) : undefined}
      >
        <div className="ncs-card-value">{count}</div>
        <div className="ncs-card-label">{label}</div>
      </div>
    );
  };

  return (
    <div className="ncs-widget" style={{'minWidth': '660px'}}>
      <h4 className="ncs-title">New Customers Secured</h4>

      {loading ? (
        <div className="ncs-state">Loading…</div>
      ) : error ? (
        <div className="ncs-state ncs-error">Unable to load data.</div>
      ) : isSelf ? (
        // Sales: Week / MTD / YTD as KPI cards.
        <div className="ncs-cards">
          {renderCard("Week To Date", totals.week, ranges.week, scopeExtra)}
          {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
          {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
        </div>
      ) : (
        // Sales Director / President: overall totals + per-rep breakdown.
        <div className="ncs-overview">
          <div className="ncs-cards">
            {renderCard("Month To Date", totals.mtd, ranges.month, scopeExtra)}
            {renderCard("Year To Date", totals.ytd, ranges.year, scopeExtra)}
          </div>

          <table className="ncs-table">
            <thead>
              <tr>
                <th className="ncs-rowhead">Sales Person</th>
                <th>Month To Date</th>
                <th>Year To Date</th>
              </tr>
            </thead>
            <tbody>
              {reps.length > 0 ? (
                reps.map((rep) => {
                  let mtdClickable = rep.mtd > 0;
                  let ytdClickable = rep.ytd > 0;
                  return (<tr key={rep.repName}>
                    <td className="ncs-rep">{rep.repName}</td>
                    <td className="ncs-num" style={{'cursor':'pointer'}} onClick={mtdClickable ? () => openListing(ranges.week, scopeExtra, rep.salesPersonId) : undefined}>{rep.mtd}</td>
                    <td className="ncs-num" style={{'cursor':'pointer'}} onClick={ytdClickable ? () => openListing(ranges.year, scopeExtra, rep.salesPersonId) : undefined}>{rep.ytd}</td>
                  </tr>)
                })
              ) : (
                <tr>
                  <td colSpan={3} className="ncs-empty">
                    No new customers secured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { NewCustomersSecured };

export default connect(mapStateToProps)(NewCustomersSecured);
